/**
 * Il marchio di un prodotto o di un servizio (26/10/2026). Lorenzo: "mettere i loghi
 * di GoDeal, Hostinger, Aura OS, Stripe... per far capire sempre a colpo d'occhio di
 * cosa stiamo parlando".
 *
 *  - i nostri prodotti (GoDeal, Aura OS, Masamune) sono immagini in public/brands;
 *  - i servizi di terzi vengono da Simple Icons (brandMarks.ts) e si disegnano nel
 *    loro colore su una tessera chiara; i marchi quasi neri prendono il colore del
 *    testo, cosi' restano leggibili anche col tema scuro;
 *  - tutto il resto ha le iniziali su una tessera neutra: mai un logo inventato.
 *
 * `brandFor(testo)` riconosce il marchio dal nome di un fornitore, da un'etichetta
 * della banca o dal nome di un piano ("HighLevel Agency (GoDeal)" -> godeal).
 */
import { BRAND_MARKS } from './brandMarks';
import { cn, nameInitials } from '../../lib/utils';
import './brand.css';

// `scura`: il marchio in bianco su tessera nera, come nella barra della piattaforma GoDeal (Lorenzo, 26/10/2026).
const IMMAGINI: Record<string, { src: string; title: string; piena?: boolean; scura?: boolean }> = {
  godeal: { src: '/brands/godeal-bianco.png', title: 'GoDeal', scura: true },
  auraos: { src: '/brands/auraos.png', title: 'Aura OS' },
  masamune: { src: '/logos/m.svg', title: 'Masamune' },
};

// L'ordine conta: i casi piu' specifici prima (Google Ads prima di Google).
const RICONOSCI: Array<[RegExp, string]> = [
  [/go ?deal|highlevel|leadconnector|lc phone/i, 'godeal'],
  [/aura ?os/i, 'auraos'],
  [/masamune/i, 'masamune'],
  [/stripe/i, 'stripe'],
  [/hostinger/i, 'hostinger'],
  [/google ?ads/i, 'googleads'],
  [/google ?play/i, 'googleplay'],
  [/google ?drive/i, 'googledrive'],
  [/gmail/i, 'gmail'],
  [/google/i, 'google'],
  [/whats ?app|waautoreply/i, 'whatsapp'],
  [/instagram/i, 'instagram'],
  [/facebook/i, 'facebook'],
  [/\bmeta\b|metapay|mv4b/i, 'meta'],
  [/tiktok/i, 'tiktok'],
  [/linkedin/i, 'linkedin'],
  [/youtube/i, 'youtube'],
  [/openai|chat ?gpt/i, 'openai'],
  [/anthropic|claude/i, 'claude'],
  [/\bapple\b|apple\.com/i, 'apple'],
  [/canva/i, 'canva'],
  [/cloudflare/i, 'cloudflare'],
  [/digital ?ocean/i, 'digitalocean'],
  [/\bmake(\.com)?\b/i, 'make'],
  [/\brender(\.com)?\b/i, 'render'],
  [/twilio/i, 'twilio'],
  [/prime ?video|amazon prime/i, 'primevideo'],
  [/amazon/i, 'amazon'],
  [/wordpress/i, 'wordpress'],
  [/shopify/i, 'shopify'],
  [/figma/i, 'figma'],
  [/notion/i, 'notion'],
  [/slack/i, 'slack'],
  [/github/i, 'github'],
  [/vercel/i, 'vercel'],
  [/netlify/i, 'netlify'],
  [/trello/i, 'trello'],
  [/\bzoom\b/i, 'zoom'],
  [/discord/i, 'discord'],
  [/telegram/i, 'telegram'],
  [/paypal/i, 'paypal'],
  [/mailchimp/i, 'mailchimp'],
  [/brevo|sendinblue/i, 'brevo'],
  [/zapier/i, 'zapier'],
  [/airtable/i, 'airtable'],
  [/dropbox/i, 'dropbox'],
  [/\bn8n\b/i, 'n8n'],
  [/supabase/i, 'supabase'],
  [/firebase/i, 'firebase'],
  [/vimeo/i, 'vimeo'],
  [/adobe/i, 'adobe'],
  [/spotify/i, 'spotify'],
  [/airbnb/i, 'airbnb'],
  [/tripadvisor/i, 'tripadvisor'],
  [/glovo/i, 'glovo'],
  [/\buber\b/i, 'uber'],
];

/** Il marchio riconosciuto in uno o piu' testi (nome, etichetta, piano), o null. */
export function brandFor(...testi: Array<string | null | undefined>): string | null {
  const t = testi.filter(Boolean).join(' ');
  if (!t) return null;
  for (const [re, key] of RICONOSCI) if (re.test(t)) return key;
  return null;
}

/** Il nome leggibile di un marchio ("hostinger" -> "Hostinger"). */
export function brandTitle(key: string | null | undefined): string | null {
  if (!key) return null;
  return IMMAGINI[key]?.title ?? BRAND_MARKS[key]?.title ?? null;
}

function scuro(hex: string): boolean {
  const n = parseInt(hex.replace('#', ''), 16);
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b < 48;
}

interface BrandLogoProps {
  /** Chiave del marchio (es. "stripe"); se manca si prova a riconoscerlo da `name`. */
  brand?: string | null;
  /** Il nome della cosa: serve per riconoscere il marchio e per le iniziali. */
  name: string;
  size?: number;
  className?: string;
}

export function BrandLogo({ brand, name, size = 32, className }: BrandLogoProps) {
  const key = brand ?? brandFor(name);
  const img = key ? IMMAGINI[key] : undefined;
  const mark = key && !img ? BRAND_MARKS[key] : undefined;
  const titolo = img?.title ?? mark?.title ?? name;
  const stile = { width: size, height: size, borderRadius: Math.round(size * 0.28) };

  if (img?.piena) {
    return <img className={cn('brand-logo brand-logo--piena', className)} src={img.src} alt={titolo} title={titolo} style={stile} />;
  }
  if (img) {
    return (
      <span className={cn('brand-logo', img.scura && 'brand-logo--scura', className)} style={stile} title={titolo}>
        <img src={img.src} alt={titolo} style={img.scura ? { width: '54%', height: '54%' } : { width: '74%', height: '74%' }} />
      </span>
    );
  }
  if (mark) {
    return (
      <span className={cn('brand-logo', className)} style={stile} title={titolo}>
        <svg viewBox="0 0 24 24" width="58%" height="58%" aria-label={titolo} role="img">
          <path d={mark.path} fill={scuro(mark.hex) ? 'currentColor' : mark.hex} />
        </svg>
      </span>
    );
  }
  return (
    <span className={cn('brand-logo brand-logo--iniziali', className)} style={{ ...stile, fontSize: Math.max(9, Math.round(size * 0.36)) }} title={titolo}>
      {nameInitials(name)}
    </span>
  );
}
